import Vue from 'vue';
import Vuex from 'vuex';
// modules
// eslint-disable-next-line import/no-cycle
import access from '@advisor/store/modules/access';
// eslint-disable-next-line import/no-cycle
import settings from '@advisor/store/modules/settings';
// eslint-disable-next-line import/no-cycle
import goal from '@advisor/store/modules/goal';
// eslint-disable-next-line import/no-cycle
import user from '@advisor/store/modules/user';
// eslint-disable-next-line import/no-cycle
import client from '@advisor/store/modules/client';
// eslint-disable-next-line import/no-cycle
import auth from '@advisor/store/modules/auth';
// eslint-disable-next-line import/no-cycle
import aum from '@advisor/store/modules/aum';
// eslint-disable-next-line import/no-cycle
import advisor from '@advisor/store/modules/advisor';
import notification from '@advisor/store/modules/notification';
import investmentSetup from '@advisor/store/modules/investmentSetup';
// eslint-disable-next-line import/no-cycle
import household from '@advisor/store/modules/household';
// eslint-disable-next-line import/no-cycle
import manageFirm from '@advisor/store/modules/manageFirm';
import ux from '@advisor/store/modules/ux';
// eslint-disable-next-line import/no-cycle
import custodian from '@advisor/store/modules/custodian';
// eslint-disable-next-line import/no-cycle
import moneyTransfer from '@advisor/store/modules/moneyTransfer';
// eslint-disable-next-line import/no-cycle
import account from '@advisor/store/modules/account';
// eslint-disable-next-line import/no-cycle
import onboardingRia from '@advisor/store/modules/onboardingRia';
// eslint-disable-next-line import/no-cycle
import transactions from '@advisor/store/modules/transactions';
// eslint-disable-next-line import/no-cycle
import holdings from '@advisor/store/modules/holdings';
// eslint-disable-next-line import/no-cycle
import beneficiaries from '@advisor/store/modules/beneficiaries';
import accountDetails from '@advisor/store/modules/accountDetails';
import fromS3 from '@advisor/util/common/s3';
// default store
import Store from '@advisor/models/store/index';
// eslint-disable-next-line import/no-cycle
import investmentModel from '@advisor/store/modules/investmentModel';
import cognitoUser from '@advisor/store/modules/cognitoUser';
import rwOptions from '@advisor/store/modules/rwOptions';

// eslint-disable-next-line import/no-cycle
import segmentSettings from '@advisor/store/modules/segmentSettings';
import documents from '@advisor/store/modules/documents';
import Manifest from '@advisor/util/common/manifest';
import riskTolerance from '@advisor/store/modules/riskTolerance';
import accountTransfer from '@advisor/store/modules/accountTransfer';
import worker from '@advisor/store/modules/worker';
import exceptionHandler from '@advisor/util/exceptionHandler';
import {getTokens, getUser, NoCurrentUserError} from '@simpleinvest/front-end-auth';
import accountOpening from '@advisor/store/modules/apexAccountOpening';
import bankLinking from '@advisor/store/modules/bankLinking';

Vue.use(Vuex);

const {VUE_APP_ENV, VUE_APP_IS_LOCAL} = process.env;
const {RIA_ID} = window.rwOptions;

const getTokensHandled = async () => {
  try {
    // check user login status
    await getTokens();
  } catch (error) {
    if (error instanceof NoCurrentUserError) {
      console.log('no logged-in user');
    }
  }
};

// No longer fetch firm data from public API before authentication.
// Use RIA_ID from rwOptions config instead of calling searchRiaFirmFromUrl API.
export const getRiaIdForUnauthorizedRoutes = () => RIA_ID;

// Use only hardcoded safe values for pre-login state (see settings.js for details)
const stateObj = () => new Store();

const getters = {};

export const actions = {
  // eslint-disable-next-line require-await
  async setLoading({commit}, payload) {
    commit('SET_LOADING', payload);
  },

  // eslint-disable-next-line complexity,  max-lines-per-function, max-statements
  async getLayout({dispatch, state}, toState) {
    try {
      let riaId;

      riaId = await dispatch('getRiaData');

      if (!riaId && (VUE_APP_ENV === 'local' || VUE_APP_IS_LOCAL === 'true')) {
        riaId = RIA_ID;
      }

      if (!riaId) {
        // we should always be able to get ria settings
        // eslint-disable-next-line no-restricted-globals
        location.href = `${window.BASE_URL}/_404`;

        return;
      }

      getTokensHandled();

      // Determine if user is authenticated (Cognito or Azure SSO)
      let isAuthenticated = false;

      try {
        await getUser();
        isAuthenticated = true;
      } catch {
        // Not a Cognito user — check for Azure SSO token (only 'token' means SSO completed)
        // 'ssoBtn' alone means SSO redirect started but hasn't completed yet - no token available
        if (sessionStorage.getItem('token')) {
          isAuthenticated = true;
        }
      }

      // Only call APIs after authentication; before login, use hardcoded state values
      if (isAuthenticated) {
        const riaSettings = await dispatch('settings/getRiaSettings', {riaId});

        if (riaSettings) {
          await dispatch('applyRiaSettings', {riaSettings, riaId, toState});
        }
      } else {
        // Pre-login: set title from hardcoded defaults, but do NOT set isLayoutSet
        // so getLayout re-fires after login completes and token is available
        document.title = state.settings.ria.companyLegalName;
      }
    } catch (error) {
      exceptionHandler.handleGenericException('index.getLayout', error);
    } finally {
      // apple-touch settings
      if (state.settings.ria.squareLogo && state.settings.ria.headerColor) {
        const manifestSettings = {
          squareLogo: state.settings.ria.squareLogo,
          headerColor: state.settings.ria.headerColor,
        };

        Manifest.createManifest(manifestSettings);
      }
    }
  },

  async getRiaData() {
    try {
      try {
        // get riaId
        const riaUser = await getUser();

        return parseInt(riaUser.riaId, 10);
      } catch (error) {
        if (error instanceof NoCurrentUserError) {
          return await getRiaIdForUnauthorizedRoutes();
        }

        return false;
      }
    } catch (error) {
      exceptionHandler.handleGenericException('index.getRiaData', error);

      return false;
    }
  },

  // eslint-disable-next-line no-unused-vars
  async applyRiaSettings({dispatch, commit, state}, {riaSettings, riaId}) {
    const {enterpriseId} = riaSettings;

    if (!state.settings.enterprise.featuresLoaded && riaSettings.enterpriseId) {
      await dispatch('settings/getEnterpriseFeatures', {enterpriseId});
    }

    commit('SET_DEFAULT_STATE', {
      favicon: riaSettings.riaFavicon ? fromS3(riaSettings.riaFavicon) : '',
      riaId,
      enterpriseId,
    });

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    setFavicon(state.favicon);

    document.title = riaSettings.companyLegalName;
  },

  getRiaId(context) {
    return context.state.riaId;
  },

  setNotificationBellFlag({commit}, payload) {
    commit('SET_NOTIFICATION_BELL_FLAG', payload);
  },
};

// eslint-disable-next-line func-style
function setFavicon(path) {
  $('#favicon').attr('href', path);
}

// mutations
export const mutations = {
  SET_LOADING(state, payload) {
    state.isLoading = payload;
  },
  SET_DEFAULT_STATE(state, payload) {
    state.isLayoutSet = true;
    state.favicon = payload.favicon;
    state.riaId = payload.riaId;
    state.enterpriseId = payload.enterpriseId;
    state.chatPlacement = 'right';
    state.chatVisible = true;
  },

  SET_NOTIFICATION_BELL_FLAG(state, payload) {
    state.notificationBellFlag = payload;
  },
};

export default new Vuex.Store({
  state: stateObj,
  getters,
  actions,
  mutations,
  modules: {
    user,
    access,
    settings,
    goal,
    client,
    household,
    manageFirm,
    auth,
    aum,
    notification,
    investmentSetup,
    advisor,
    ux,
    custodian,
    investmentModel,
    account,
    cognitoUser,
    rwOptions,
    segmentSettings,
    moneyTransfer,
    documents,
    transactions,
    holdings,
    beneficiaries,
    accountDetails,
    riskTolerance,
    onboardingRia,
    accountTransfer,
    worker,
    accountOpening,
    bankLinking,
  },
});
